import React, { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartDataset, ChartOptions, TooltipItem } from 'chart.js';
import { SpotInterface } from 'types';
import { compactCurrency, currencyWithoutCents } from 'utils/currency';
import { getNth } from 'utils/helpers';
import { useRevenueContext } from 'contexts/RevenueContext';
import { HTTPError } from 'clients/HTTPClient';
import WidgetBox from 'components/widgets/WidgetBox';
import AGMessage from 'components/AGMessage/AGMessage';
import Loader from 'components/Loader/Loader';
import ReloadIcon from 'components/Icons/ReloadIcon';
import InfoBox from './InfoBox';
import { ChartId, filteredLegendOptions, getPacingGraph } from './helpers';
import styles from './RevenueWidgets.module.css';

type PacingProps = {
    spot: SpotInterface;
};

const options: ChartOptions<'line'> = {
    scales: {
        x: {
            ticks: {
                callback: function (val) {
                    const value = Number(val) + 1;
                    return `${value}${getNth(value)}`;
                },
            },
            title: {
                display: true,
                text: 'Days',
            },
        },
        y: {
            grid: { display: true, borderDash: [2] },
            ticks: {
                callback(tickValue) {
                    return typeof tickValue === 'number' ? compactCurrency.format(tickValue) : '';
                },
                maxTicksLimit: 4,
            },
        },
    },
    interaction: {
        mode: 'x',
        intersect: false,
    },
    plugins: {
        tooltip: {
            callbacks: {
                label(tooltipItem: TooltipItem<'line'>): string | string[] {
                    return typeof tooltipItem.raw === 'number'
                        ? `${tooltipItem.dataset.label} - ${currencyWithoutCents.format(tooltipItem.raw)}`
                        : tooltipItem.formattedValue;
                },
                title(tooltipItems: TooltipItem<'line'>[]): string | string[] {
                    const day = Number(tooltipItems[0].label);
                    return `${day}${getNth(day)}`;
                },
            },
        },
        legend: filteredLegendOptions,
    },
};

function Pacing({ spot }: PacingProps): JSX.Element {
    const { getPacingReport, pacingReport: report } = useRevenueContext();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [graphData, setGraphData] = useState<{
        datasets: Array<ChartDataset<'line'>>;
        labels: number[];
    }>({
        datasets: [],
        labels: [],
    });

    const getRevenueData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getPacingReport(spot.uuid);
            setGraphData(getPacingGraph(response));
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid]);

    useEffect(() => {
        getRevenueData();
    }, [spot.uuid, getRevenueData]);

    return (
        <WidgetBox title="Current Month Pacing">
            <>
                {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}
                {loading || !report ? (
                    <Loader centered style={{ marginTop: '20px' }} />
                ) : report.previous_month.length > 0 ? (
                    <div>
                        <InfoBox
                            header={`Gross Revenue through ${report.uplift.current_month_to_date}`}
                            data={report.uplift.current_month_to_date_gross_revenue}
                        />

                        <div id={ChartId.PACING_CHART} style={{ paddingBlock: '20px' }}>
                            <Line
                                data={{
                                    labels: graphData.labels,
                                    datasets: graphData.datasets,
                                }}
                                options={options}
                                style={{ maxHeight: '260px' }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.emptyChartBox}>
                        <ReloadIcon />
                        <h3>Pacing coming soon</h3>
                        <p>We need at least 1 full month of data to show pacing.</p>
                    </div>
                )}
            </>
        </WidgetBox>
    );
}

export default Pacing;
